import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  HiOutlineCollection,
  HiOutlineCreditCard,
  HiOutlineRefresh,
  HiOutlineUsers,
} from "react-icons/hi";
import {
  PaDataTable,
  PaEmptyState,
  PaMetricCard,
  PaNavigationContextBanner,
  PaPageHeader,
  PaSkeleton,
  PaTabs,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import { platformRoleAccess } from "helpers/RolesPermission";
import {
  fetchPlatformBillingOverview,
  fetchPlatformBillingPlans,
  fetchPlatformStripeCustomers,
  syncPlatformStripeInvoices,
} from "store/services/platformAdmin.services";
import { buildSubscriptionStatusRows, formatCurrency } from "./billingOverview.utils";
import StripeManagementCharts from "./StripeManagementCharts";
import StripeCustomersTab from "./StripeCustomersTab";
import StripeDashboardLink from "./StripeDashboardLink";
import { getNavigationContext } from "../platformAdminNavigation.utils";
import {
  buildPlanAssignmentRows,
  buildStripeOverviewMetrics,
  formatPlanCatalogAmount,
  getStripeManagementSubtitle,
  getStripeManagementTabs,
} from "./stripeManagement.utils";

const METRIC_ICONS = {
  stripe_customers: HiOutlineUsers,
  products: HiOutlineCollection,
  prices: HiOutlineCreditCard,
  paying_tenants: HiOutlineUsers,
  last_sync: HiOutlineRefresh,
};

const StripeManagement = ({ initialTab = null }) => {
  const canManagePlans = platformRoleAccess("platform.plans.manage");
  const canViewBilling = platformRoleAccess("platform.billing.view");

  const tabs = useMemo(
    () => getStripeManagementTabs({ canManagePlans, canViewBilling }),
    [canManagePlans, canViewBilling]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const focus = searchParams.get("focus");
  const customerFilter = searchParams.get("filter") || "all";
  const navigationContext = useMemo(() => getNavigationContext(focus), [focus]);

  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab && tabs.some((tab) => tab.id === initialTab)) {
      return initialTab;
    }

    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && tabs.some((tab) => tab.id === tabFromUrl)) {
      return tabFromUrl;
    }

    return tabs[0]?.id || "plans";
  });
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(null);
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [customerSummary, setCustomerSummary] = useState(null);
  const [stripeDashboard, setStripeDashboard] = useState(null);

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id || "plans");
    }
  }, [activeTab, tabs]);

  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tabId === tabs[0]?.id) {
            next.delete("tab");
          } else {
            next.set("tab", tabId);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, tabs]
  );

  const handleCustomerFilterChange = useCallback(
    (nextFilter) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!nextFilter || nextFilter === "all") {
            next.delete("filter");
          } else {
            next.set("filter", nextFilter);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const loadPlans = useCallback(async () => {
    if (!canManagePlans) {
      return;
    }

    setPlansLoading(true);
    setPlansError(null);

    try {
      const rows = await fetchPlatformBillingPlans();
      setPlans(rows);
    } catch (err) {
      setPlans([]);
      setPlansError(err?.message || "Failed to load Stripe plans");
    } finally {
      setPlansLoading(false);
    }
  }, [canManagePlans]);

  const loadBilling = useCallback(async () => {
    if (!canViewBilling) {
      return null;
    }

    setBillingLoading(true);
    setBillingError(null);

    try {
      const data = await fetchPlatformBillingOverview();
      setBilling(data);
      return data;
    } catch (err) {
      setBilling(null);
      setBillingError(err?.message || "Failed to load Stripe billing data");
      return null;
    } finally {
      setBillingLoading(false);
    }
  }, [canViewBilling]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const loadCustomerSummary = useCallback(async () => {
    if (!canViewBilling) {
      return;
    }

    try {
      const data = await fetchPlatformStripeCustomers({});
      setCustomerSummary(data?.summary ?? null);
      setStripeDashboard(data?.stripe_dashboard ?? null);
    } catch {
      setCustomerSummary(null);
    }
  }, [canViewBilling]);

  useEffect(() => {
    loadCustomerSummary();
  }, [loadCustomerSummary]);

  const handleSyncInvoices = async () => {
    setSyncing(true);
    setSyncMessage(null);
    setBillingError(null);

    try {
      const result = await syncPlatformStripeInvoices();

      if (result?.skipped) {
        setSyncMessage(result.message || "Stripe invoice sync skipped.");
      } else {
        setSyncMessage(
          `Synced ${result?.invoices_upserted ?? 0} invoice(s) across ${result?.tenants_processed ?? 0} tenant(s).`
        );
      }

      await loadBilling();
      await loadCustomerSummary();
    } catch (err) {
      setBillingError(err?.message || "Failed to sync Stripe invoices");
    } finally {
      setSyncing(false);
    }
  };

  const handleCustomerSummaryChange = useCallback((summary, dashboard) => {
    setCustomerSummary(summary);
    if (dashboard) {
      setStripeDashboard(dashboard);
    }
  }, []);

  const overviewMetrics = useMemo(
    () => buildStripeOverviewMetrics(plans, billing, customerSummary, stripeDashboard),
    [plans, billing, customerSummary, stripeDashboard]
  );
  const assignmentRows = useMemo(
    () => buildPlanAssignmentRows(billing?.plan_breakdown),
    [billing]
  );
  const statusRows = useMemo(
    () => buildSubscriptionStatusRows(billing?.subscription_status_breakdown),
    [billing]
  );
  const revenueSummary = billing?.revenue_summary;

  if (!canManagePlans && !canViewBilling) {
    return (
      <PaAccessDenied
        title="Stripe management"
        subtitle="Plans, customers, and billing sync"
        description="You need platform.plans.manage or platform.billing.view."
      />
    );
  }

  const renderCustomersTab = () => (
    <StripeCustomersTab
      initialCustomerFilter={customerFilter}
      onCustomerFilterChange={handleCustomerFilterChange}
      onSummaryChange={handleCustomerSummaryChange}
    />
  );

  const renderPlansTab = () => {
    if (plansLoading) {
      return <PaSkeleton variant="table" lines={6} />;
    }

    if (plansError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {plansError}
        </div>
      );
    }

    if (plans.length === 0) {
      return (
        <PaEmptyState
          title="No Stripe plans"
          message="Configure STRIPE_SECRET_KEY or add products in Stripe."
        />
      );
    }

    return (
      <div className="space-y-6">
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Stripe plan catalog</h2>
              <p className="mt-1 text-sm text-gray-500">
                Active products and prices from your Stripe account. Plan changes are made in the
                Stripe Dashboard (view-only here per platform policy).
              </p>
            </div>
            {stripeDashboard?.products_url ? (
              <StripeDashboardLink
                href={stripeDashboard.products_url}
                className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
              >
                Open Stripe products
              </StripeDashboardLink>
            ) : null}
          </div>
          <div className="mt-4">
            <PaDataTable
              columns={[
                {
                  key: "product_name",
                  label: "Product",
                  render: (row) =>
                    row.stripe_dashboard_product_url ? (
                      <StripeDashboardLink
                        href={row.stripe_dashboard_product_url}
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                        title="Open product in Stripe"
                      >
                        {row.product_name}
                      </StripeDashboardLink>
                    ) : (
                      row.product_name
                    ),
                },
                {
                  key: "price_id",
                  label: "Price ID",
                  render: (row) =>
                    row.stripe_dashboard_price_url ? (
                      <StripeDashboardLink
                        href={row.stripe_dashboard_price_url}
                        className="font-mono text-xs text-indigo-600 hover:text-indigo-700"
                        title="Open price in Stripe"
                      >
                        {row.price_id}
                      </StripeDashboardLink>
                    ) : (
                      <span className="font-mono text-xs">{row.price_id}</span>
                    ),
                },
                {
                  key: "amount",
                  label: "Amount",
                  render: (row) => formatPlanCatalogAmount(row),
                },
                { key: "interval", label: "Interval", render: (row) => row.interval || "—" },
                { key: "nickname", label: "Nickname", render: (row) => row.nickname || "—" },
              ]}
              rows={plans.map((row, index) => ({ ...row, id: row.price_id || index }))}
              rowKey="id"
            />
          </div>
        </section>

        {canViewBilling ? (
          billingLoading ? (
            <PaSkeleton variant="card" />
          ) : (
            <StripeManagementCharts billing={billing} plans={plans} />
          )
        ) : null}
      </div>
    );
  };

  const renderAssignmentsTab = () => {
    if (billingLoading) {
      return <PaSkeleton variant="table" lines={5} />;
    }

    if (billingError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {billingError}
        </div>
      );
    }

    return (
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900">Tenant plan assignments</h2>
        <p className="mt-1 text-sm text-gray-500">
          How tenants map to internal plan names matched against the Stripe catalog.
        </p>
        {assignmentRows.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500">No plan assignments recorded yet.</p>
        ) : (
          <div className="mt-4">
            <PaDataTable
              columns={[
                { key: "plan_name", label: "Plan", render: (row) => row.plan_name },
                {
                  key: "tenant_count",
                  label: "Tenants",
                  headerClassName: "text-right",
                  cellClassName: "text-right tabular-nums",
                  render: (row) => row.tenant_count,
                },
                {
                  key: "paying_count",
                  label: "Paying",
                  headerClassName: "text-right",
                  cellClassName: "text-right tabular-nums",
                  render: (row) => row.paying_count,
                },
                {
                  key: "trial_count",
                  label: "Trial",
                  headerClassName: "text-right",
                  cellClassName: "text-right tabular-nums",
                  render: (row) => row.trial_count,
                },
                {
                  key: "failed_count",
                  label: "Failed",
                  headerClassName: "text-right",
                  cellClassName: "text-right tabular-nums",
                  render: (row) => row.failed_count,
                },
                {
                  key: "estimated_revenue",
                  label: "Est. MRR",
                  headerClassName: "text-right",
                  cellClassName: "text-right",
                  render: (row) => row.estimated_revenue,
                },
              ]}
              rows={assignmentRows}
              rowKey="id"
            />
          </div>
        )}
      </section>
    );
  };

  const renderSubscriptionsTab = () => {
    if (billingLoading) {
      return <PaSkeleton variant="card" />;
    }

    if (billingError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {billingError}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <StripeManagementCharts billing={billing} plans={plans} />

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Subscription status breakdown</h2>
              <p className="mt-1 text-sm text-gray-500">
                Stripe-reported subscription statuses across all tenants with billing profiles. For a
                tenant-by-tenant list of current and historical Stripe customers, use the{" "}
                <button
                  type="button"
                  onClick={() => handleTabChange("customers")}
                  className="font-medium text-indigo-600 hover:text-indigo-700"
                >
                  Stripe customers
                </button>{" "}
                tab.
              </p>
            </div>
            {stripeDashboard?.subscriptions_url ? (
              <StripeDashboardLink
                href={stripeDashboard.subscriptions_url}
                className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
              >
                Open Stripe subscriptions
              </StripeDashboardLink>
            ) : null}
          </div>
          {statusRows.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">No subscription status data.</p>
          ) : (
            <div className="mt-4">
              <PaDataTable
                columns={[
                  { key: "status", label: "Status", render: (row) => row.status },
                  {
                    key: "tenant_count",
                    label: "Tenants",
                    headerClassName: "text-right",
                    cellClassName: "text-right tabular-nums",
                    render: (row) => row.tenant_count,
                  },
                ]}
                rows={statusRows}
                rowKey="id"
              />
            </div>
          )}
        </section>
      </div>
    );
  };

  const renderSyncTab = () => (
    <div className="space-y-6">
      {navigationContext &&
      (focus === "collected_30d" || focus === "collected_all_time") &&
      revenueSummary ? (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-indigo-700">
              Collected (30 days)
            </p>
            <p className="mt-1 text-2xl font-semibold text-indigo-950">
              {formatCurrency(
                revenueSummary.collected_last_30_days_cents,
                revenueSummary.currency
              )}
            </p>
          </div>
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-indigo-700">
              Collected (all time)
            </p>
            <p className="mt-1 text-2xl font-semibold text-indigo-950">
              {formatCurrency(revenueSummary.collected_all_time_cents, revenueSummary.currency)}
            </p>
          </div>
        </section>
      ) : null}

      {billingError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {billingError}
        </div>
      ) : null}

      {syncMessage ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {syncMessage}
        </div>
      ) : null}

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Invoice history sync</h2>
            <p className="mt-1 text-sm text-gray-500">
              Pull Stripe invoice records into the platform database for revenue reporting. A
              scheduled job also runs nightly at 03:00 Sydney time.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSyncInvoices}
            disabled={syncing || billingLoading}
            className="inline-flex shrink-0 items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Last sync
            </dt>
            <dd className="mt-1 text-sm font-semibold text-gray-900">
              {revenueSummary?.last_stripe_sync_at ? (
                stripeDashboard?.invoices_url ? (
                  <StripeDashboardLink
                    href={stripeDashboard.invoices_url}
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                    title="Open invoices in Stripe"
                  >
                    {new Date(revenueSummary.last_stripe_sync_at).toLocaleString()}
                  </StripeDashboardLink>
                ) : (
                  new Date(revenueSummary.last_stripe_sync_at).toLocaleString()
                )
              ) : stripeDashboard?.invoices_url ? (
                <StripeDashboardLink
                  href={stripeDashboard.invoices_url}
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                  title="Open invoices in Stripe"
                >
                  Never
                </StripeDashboardLink>
              ) : (
                "Never"
              )}
            </dd>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Synced records
            </dt>
            <dd className="mt-1 text-sm font-semibold text-gray-900">
              {revenueSummary?.data_sources?.synced_records ?? "—"}
            </dd>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Webhook records
            </dt>
            <dd className="mt-1 text-sm font-semibold text-gray-900">
              {revenueSummary?.data_sources?.local_webhook_records ?? "—"}
            </dd>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Stripe API
            </dt>
            <dd className="mt-1 text-sm font-semibold text-gray-900">
              {revenueSummary?.stripe_available ? "Connected" : "Not configured"}
            </dd>
          </div>
        </dl>
      </section>

      {canViewBilling ? (
        <p className="text-sm text-gray-500">
          Synced data powers collected revenue on the{" "}
          <Link to="/billing" className="font-medium text-indigo-600 hover:text-indigo-700">
            Financial overview
          </Link>
          .
        </p>
      ) : null}
    </div>
  );

  const tabContent = {
    customers: renderCustomersTab(),
    plans: renderPlansTab(),
    assignments: renderAssignmentsTab(),
    subscriptions: renderSubscriptionsTab(),
    sync: renderSyncTab(),
  };

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title={initialTab === "plans" ? "Plans" : "Stripe management"}
        subtitle={
          initialTab === "plans"
            ? "Stripe product and price catalog for tenant subscriptions."
            : getStripeManagementSubtitle({
                plansCount: plans.length,
                billing,
                customerSummary,
              })
        }
        actions={
          initialTab === "plans" && canViewBilling ? (
            <Link
              to="/stripe"
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Stripe management →
            </Link>
          ) : canViewBilling ? (
            <Link
              to="/billing"
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Financial overview →
            </Link>
          ) : null
        }
      />

      {navigationContext ? (
        <div className="mb-4">
          <PaNavigationContextBanner
            title={navigationContext.title}
            description={navigationContext.description}
          />
        </div>
      ) : null}

      <section className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        {overviewMetrics.map((metric) => {
          const Icon = METRIC_ICONS[metric.id] || HiOutlineCollection;
          const card = (
            <PaMetricCard
              label={metric.label}
              value={metric.value}
              icon={<Icon className="h-5 w-5" aria-hidden="true" />}
            />
          );

          if (metric.externalUrl) {
            return (
              <a
                key={metric.id}
                href={metric.externalUrl}
                target="_blank"
                rel="noreferrer"
                className="block"
                title={`Open ${metric.label} in Stripe`}
              >
                {card}
              </a>
            );
          }

          return metric.to ? (
            <Link key={metric.id} to={metric.to} className="block">
              {card}
            </Link>
          ) : (
            <div key={metric.id}>{card}</div>
          );
        })}
      </section>

      {tabs.length > 1 ? (
        <PaTabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
      ) : null}

      <div className="mt-6">{tabContent[activeTab] || tabContent[tabs[0]?.id]}</div>
    </PlatformAdminLayout>
  );
};

export default StripeManagement;
